import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import { DYNAMIC_TABLE, DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET, getApiPath, LIST, PROMPT_EDITOR_CREATE } from "../../api/ApiConst";
import ApiCaller from "../../api/ApiCaller";

const PromptEditor = () => {
    const [selectedTables, setSelectedTables] = useState([]);
    const [allTableOpions, setAllTableOpions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [resultData, setResultData] = useState(null);
    const quillRef = useRef(null);

    useEffect(()=>{
        fetchData(1, 10);
    },[])

    const fetchData = async (page, size = 10) => {
        setTableLoading(true);
        var jsonRequest = {
            "page": page - 1,
            "size": size,
        };
        ApiCaller.callPostApi(getApiPath(DYNAMIC_TABLE, LIST), jsonRequest, {}, (response) => {
            let allOptions = response?.data?.map(data=>{
                return {...data,
                    value:data?._id,
                    label:data?.name
                }
            })
            setAllTableOpions(allOptions);
            setTableLoading(false);
        }, (error) => {
            setTableLoading(false);
            console.error(error);
        })
    };

    const getTableAttributeListForTableColumns = (table) => {
            var jsonRequest = {
                "page": 0,
                "size": 100,
                searchFilters: [],
                sortRules: [],
            };
            let attributesUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll("{dynamic-table-id}", (table?.value))
            
            ApiCaller.callPostApi(attributesUrl, jsonRequest, {}, (response) => {
                let updatedTableData = [...selectedTables,{
                    value:table?.value,
                    label:table?.label,
                    colunms:response?.data
                }]
                setSelectedTables(updatedTableData);
            }
        )
    }

    const handleTableChange = (selectedOptions) => {
        
        let newTable = selectedOptions?.map(option=>option?.value);
        if(selectedTables?.find(table=> !newTable?.includes(table?.value))){
            setSelectedTables(selectedTables?.filter(table=>newTable?.includes(table?.value)));
        }else{
            getTableAttributeListForTableColumns(selectedOptions?.[selectedOptions?.length - 1]);            
        }
    };

    const handleColumnClick = (table, colName) => {
        if(loading) return;
        const quill = quillRef.current?.getEditor();
        if (quill) {
            const range = quill.getSelection(true);
            
            const tag = `@${table}.${colName}`;
    
            // Add space before and after the tag for better separation
            const htmlTag = `<span style="background:#d2edfa; color:#000; font-weight:bold; padding:2px 6px; border-radius:4px;">${tag}</span>`;
    
            // Insert the styled tag and the spaces
            quill.clipboard.dangerouslyPasteHTML(range.index, `${htmlTag}`); // Adds space before and after the tag
            
            // Move the cursor after the inserted tag
            quill.setSelection(range.index + htmlTag.length + 2); // Including spaces
    
            // Reset any custom formats
            quill.format('color', false);
            quill.format('background', false);
            quill.format('bold', false);
        }
    };

    const handleSend = async () => {
        setLoading(true);
        setResultData("Please wait while we generate the schema...");
        const plainText = quillRef.current?.getEditor().getText().trim();
        if (!plainText) return;

        let payload = {
            prompt:plainText,
            model: "openai/gpt-4",
            interactive: false
        }

        ApiCaller.callPostApiWithoutAuth(
            PROMPT_EDITOR_CREATE,
            payload,
            (response) => {
                setLoading(false);
                toast.success("Schema generated successfully!");
                if(response?.schema){
                    setResultData(JSON.stringify(response?.schema, null, 2));
                }else{
                    setResultData("No schema found!");
                }
            },
            (error) => {
                setLoading(false);
                toast.error(error?.message || "Error generating schema. Please try again.");
                console.error(error);
                if(error?.id){
                    setResultData("No schema found!");
                }
            }
        );

    };

    return (
        <div style={{ padding: '30px', fontFamily: 'Inter, sans-serif' }}>
            <div
                style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '16px',
                    background: '#fafafa',
                    position: 'relative',
                }}
            >
                <label style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}>
                    Select Tables:
                </label>

                <Select
                    isDisabled={loading}
                    isMulti
                    loading={tableLoading}
                    options={allTableOpions}
                    value={selectedTables}
                    onChange={handleTableChange}
                    placeholder="Select tables..."
                    styles={{
                        control: (base) => ({
                            ...base,
                            borderRadius: '6px',
                            borderColor: '#ccc',
                            fontSize: '14px',
                        }),
                    }}
                    className="react-select-container"
                />

                <div style={{ position: 'relative', marginTop: '16px' }}>
                    <ReactQuill
                        ref={quillRef}
                        readOnly={loading}
                        theme="snow"
                        value={prompt}
                        onChange={setPrompt}
                        placeholder="Write your prompt here..."
                        style={{
                            height: '150px',
                            marginBottom: '40px',
                            fontFamily: 'monospace',
                        }}
                        modules={{
                            toolbar: false,
                        }}
                    />

                    <button
                        onClick={handleSend}
                        disabled={loading}
                        style={{
                            position: 'absolute',
                            right: '10px',
                            bottom: '10px',
                            backgroundColor: '#1890ff',
                            border: 'none',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        {loading ? (
                            <div className="loader" style={{
                                width: 20,
                                height: 20,
                                border: '2px solid white',
                                borderTop: '2px solid #1890ff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }} />
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="white"
                            >
                                <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>
            {
                selectedTables?.length > 0 && (
                    <>
                        <h4 style={{ marginTop: '20px' }}>Suggested Columns:</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {
                                selectedTables?.map((col) => (
                                    col?.colunms?.map((attribute)=>
                                        attribute?.active && (
                                        <span
                                            key={`${col?.label}-${attribute?.name}`} 
                                            onClick={() => handleColumnClick(col?.label, attribute?.name)}
                                            style={{
                                            backgroundColor: '#f0f0f0',
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            fontSize: '13px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {attribute?.name}
                                    </span>
                                )
                            )
                            ))
                            }
                        </div>
                     </>
                )
            }



            {resultData && (
                <div style={{ marginTop: '20px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: 600 }}>Result:</h4>
                    <pre style={{
                        backgroundColor: '#f7f7f7',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        padding: '12px',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '500px',
                        overflow: 'auto'
                    }}>
                        {
                            resultData
                        }
                    </pre>
                </div>
            )}
            <ToastContainer />
        </div>
    );
};

export default PromptEditor;
